import { useState } from 'react';
import { message, Modal } from 'antd';
import axios from 'axios';

export const useSettings = () => {
    const [selectedConsumer, setSelectedConsumer] = useState(null);
    const [selectedPersonId, setSelectedPersonId] = useState(null); // Currently active person_id
    const [personIdInput, setPersonIdInput] = useState(''); // Input field value

    const defaultSettings = {
        turn_off_fpe: false,
        check_pth: true,
        check_fhaa: false,
        reject_desktop: null,
        green_lane: null,
        allowed_countries: null,
        check_camera: null,
        check_camera_strict: null,
        check_camera_window_switch: null
    };

    const [settings, setSettings] = useState(defaultSettings);
    const [loading, setLoading] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    const loadConsumerSettings = async (consumerId, personId = null) => {
        setLoading(prev => ({ ...prev, loadingSettings: true }));
        try {
            console.log('Loading settings for consumer:', consumerId, 'person:', personId);

            const params = { consumer_id: consumerId };
            if (personId) {
                params.person_id = personId;
            }
            console.log(params);
            const response = await axios.get("/admin/settings/fpe", { params });

            setSettings(response.data);
            setSelectedPersonId(personId);
            setHasChanges(false);

            const targetName = personId ? `person ${personId}` : selectedConsumer?.username || 'consumer';
            message.success(`Settings loaded successfully for ${targetName}`);
        } catch (error) {
            console.error('Error loading settings:', error);
            message.error('Failed to load settings');
        } finally {
            setLoading(prev => ({ ...prev, loadingSettings: false }));
        }
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    const handleToggle = async (settingKey, apiMethods) => {
        if (!selectedConsumer) {
            message.error('Please select a consumer first');
            return;
        }

        const newValue = !settings[settingKey];
        setLoading(prev => ({ ...prev, [settingKey]: true }));

        try {
            await new Promise(resolve => setTimeout(resolve, 1000));

            handleSettingChange(settingKey, newValue);

            const targetName = selectedPersonId ? `person ${selectedPersonId}` : selectedConsumer.username;
            message.success(`Setting updated successfully for ${targetName}`);
        } catch (error) {
            message.error('Failed to update setting');
        } finally {
            setLoading(prev => ({ ...prev, [settingKey]: false }));
        }
    };

    const saveAllChanges = async () => {
        if (!selectedConsumer) {
            message.error('Please select a consumer first');
            return;
        }

        setLoading(prev => ({ ...prev, saving: true }));
        try {
            const sanitizedSettings = Object.fromEntries(
                Object.entries(settings).map(([key, value]) => [
                    key,
                    value === null ? false : value
                ])
            );

            if(sanitizedSettings['allowed_countries'] === true ||
                sanitizedSettings['allowed_countries'] === false ||
                sanitizedSettings['allowed_countries'] === []) {
                sanitizedSettings['allowed_countries'] = null;
            }

            sanitizedSettings['green_lane'] = null;

            // Prepare request body with person_id if available
            const requestBody = {
                params: {
                    consumer_id: selectedConsumer.consumer_id,
                },
                body: sanitizedSettings,
            };

            // Add person_id to params if it's selected
            if (selectedPersonId) {
                requestBody.params.person_id = selectedPersonId;
            }

            console.log(`Saving all settings for consumer ${selectedConsumer.consumer_id}${selectedPersonId ? ` and person ${selectedPersonId}` : ''}:`, settings);

            await axios.post("/admin/settings/fpe", requestBody);

            setHasChanges(false);

            const targetName = selectedPersonId ? `person ${selectedPersonId}` : selectedConsumer.username;
            message.success(`All settings saved successfully for ${targetName}`);
        } catch (error) {
            message.error('Failed to save settings');
        } finally {
            setLoading(prev => ({ ...prev, saving: false }));
        }
    };

    const resetToDefault = async () => {
        if (!selectedConsumer) {
            message.error('Please select a consumer first');
            return;
        }

        const targetName = selectedPersonId ? `person ${selectedPersonId}` : selectedConsumer.username;

        Modal.confirm({
            title: 'Reset to Default Settings',
            content: `Are you sure you want to reset all settings for ${targetName} to default values? This action cannot be undone.`,
            okText: 'Yes, Reset',
            cancelText: 'Cancel',
            okType: 'danger',
            onOk: async () => {
                setLoading(prev => ({ ...prev, resetting: true }));
                try {
                    setSettings(defaultSettings);
                    setHasChanges(true);
                    message.success(`Settings reset to default for ${targetName}`);
                } catch (error) {
                    message.error('Failed to reset settings');
                } finally {
                    setLoading(prev => ({ ...prev, resetting: false }));
                }
            }
        });
    };

    const applyPersonId = () => {
        const personId = personIdInput.trim() || null;
        setSelectedPersonId(personId);
        if (selectedConsumer) {
            loadConsumerSettings(selectedConsumer.consumer_id, personId);
        }
    };

    // Helper function to clear person selection
    const clearPersonSelection = () => {
        setSelectedPersonId(null);
        setPersonIdInput('');
        if (selectedConsumer) {
            loadConsumerSettings(selectedConsumer.consumer_id);
        }
    };

    return {
        selectedConsumer,
        setSelectedConsumer,
        selectedPersonId,
        setSelectedPersonId,
        personIdInput,
        setPersonIdInput,
        settings,
        loading,
        hasChanges,
        loadConsumerSettings,
        handleSettingChange,
        handleToggle,
        resetToDefault,
        saveAllChanges,
        applyPersonId,
        clearPersonSelection
    };
};